import { useState, useMemo, useCallback } from 'react'
import { Save, RotateCcw, Navigation } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { IconLabel, SidebarApiIcon } from '@/components/ui/app-icon'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useToast } from '@/hooks/useToast'
import { sidebarApi, type SidebarCatalogItem } from '@/lib/api'

// ─── Helpers ──────────────────────────────────────────────────────────────────

type RoleTab = 'admin' | 'worker'

const ROLE_LABEL: Record<RoleTab, React.ReactNode> = {
  admin: <IconLabel icon="admin" iconClassName="h-4 w-4">Администратор</IconLabel>,
  worker: <IconLabel icon="worker" iconClassName="h-4 w-4">Оператор</IconLabel>,
}

const GROUP_ORDER = ['main', 'settings', 'ez']

function groupItems(items: SidebarCatalogItem[]): { group: string; label: string; items: SidebarCatalogItem[] }[] {
  const map = new Map<string, { group: string; label: string; items: SidebarCatalogItem[] }>()
  for (const item of items) {
    if (!map.has(item.group)) {
      map.set(item.group, { group: item.group, label: item.group_label, items: [] })
    }
    map.get(item.group)!.items.push(item)
  }
  return GROUP_ORDER
    .filter(g => map.has(g))
    .map(g => map.get(g)!)
    .concat([...map.entries()].filter(([g]) => !GROUP_ORDER.includes(g)).map(([, v]) => v))
}

// ─── Main component ───────────────────────────────────────────────────────────

export function SidebarSettingsPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [roleTab, setRoleTab] = useState<RoleTab>('admin')
  const [isSaving, setIsSaving] = useState(false)

  // Enabled state — separate from server data so user can toggle without committing
  const [adminEnabled, setAdminEnabled] = useState<Record<string, boolean> | null>(null)
  const [workerEnabled, setWorkerEnabled] = useState<Record<string, boolean> | null>(null)

  // ── Load catalog for both roles ──────────────────────────────────────────
  const { data: adminCatalog, isLoading: loadingAdmin } = useQuery({
    queryKey: ['sidebar-catalog', 'admin'],
    queryFn: async () => {
      const { data } = await sidebarApi.getCatalog('admin')
      // seed enabled state from server if not yet seeded
      setAdminEnabled(prev =>
        prev ?? Object.fromEntries(data.map(i => [i.endpoint, i.enabled])),
      )
      return data
    },
    staleTime: 30_000,
  })

  const { data: workerCatalog, isLoading: loadingWorker } = useQuery({
    queryKey: ['sidebar-catalog', 'worker'],
    queryFn: async () => {
      const { data } = await sidebarApi.getCatalog('worker')
      setWorkerEnabled(prev =>
        prev ?? Object.fromEntries(data.map(i => [i.endpoint, i.enabled])),
      )
      return data
    },
    staleTime: 30_000,
  })

  // ── Toggle a single item ─────────────────────────────────────────────────
  const toggle = useCallback((role: RoleTab, endpoint: string, value: boolean) => {
    if (role === 'admin') setAdminEnabled(prev => ({ ...(prev ?? {}), [endpoint]: value }))
    else setWorkerEnabled(prev => ({ ...(prev ?? {}), [endpoint]: value }))
  }, [])

  const toggleAll = useCallback((role: RoleTab, value: boolean) => {
    const catalog = role === 'admin' ? adminCatalog : workerCatalog
    if (!catalog) return
    const map = Object.fromEntries(catalog.map(i => [i.endpoint, value]))
    if (role === 'admin') setAdminEnabled(map)
    else setWorkerEnabled(map)
  }, [adminCatalog, workerCatalog])

  // ── Reset to catalog defaults ────────────────────────────────────────────
  const resetDefaults = useCallback((role: RoleTab) => {
    const catalog = role === 'admin' ? adminCatalog : workerCatalog
    if (!catalog) return
    const map = Object.fromEntries(catalog.map(i => [i.endpoint, i.enabled]))
    if (role === 'admin') setAdminEnabled(map)
    else setWorkerEnabled(map)
    toast.info('Значения сброшены — нажмите «Сохранить» для применения')
  }, [adminCatalog, workerCatalog, toast])

  // ── Save ─────────────────────────────────────────────────────────────────
  const save = useCallback(async () => {
    if (!adminCatalog || !workerCatalog) return
    setIsSaving(true)
    try {
      const buildItems = (catalog: SidebarCatalogItem[], enabled: Record<string, boolean>) =>
        catalog
          .filter(i => enabled[i.endpoint] ?? i.enabled)
          .map(({ icon, label, endpoint, match }) => ({ icon, label, endpoint, match }))

      await sidebarApi.saveConfig({
        admin: buildItems(adminCatalog, adminEnabled ?? {}),
        worker: buildItems(workerCatalog, workerEnabled ?? {}),
      })

      // Invalidate sidebar cache so navbar re-renders on next page load
      await queryClient.invalidateQueries({ queryKey: ['sidebar'] })
      toast.success('Навигация сохранена')
    } catch {
      toast.error('Ошибка сохранения')
    } finally {
      setIsSaving(false)
    }
  }, [adminCatalog, workerCatalog, adminEnabled, workerEnabled, queryClient, toast])

  // ── Render helpers ───────────────────────────────────────────────────────
  const currentCatalog = roleTab === 'admin' ? adminCatalog : workerCatalog
  const currentEnabled = roleTab === 'admin' ? adminEnabled : workerEnabled
  const isLoading = loadingAdmin || loadingWorker

  const grouped = useMemo(
    () => (currentCatalog ? groupItems(currentCatalog) : []),
    [currentCatalog],
  )

  const enabledCount = useMemo(() => {
    if (!currentCatalog || !currentEnabled) return 0
    return currentCatalog.filter(i => currentEnabled[i.endpoint] ?? i.enabled).length
  }, [currentCatalog, currentEnabled])

  if (isLoading) return <LoadingSpinner className="mt-20" />

  return (
    <div className="max-w-2xl">
      {/* Page header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Navigation className="w-6 h-6 text-blue-500" />
            Навигация
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Выберите, какие разделы показывать в боковом меню
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => resetDefaults(roleTab)}
            title="Сбросить к значениям по умолчанию"
          >
            <RotateCcw className="w-4 h-4 mr-1" /> Сбросить
          </Button>
          <Button size="sm" onClick={save} isLoading={isSaving}>
            <Save className="w-4 h-4 mr-1" /> Сохранить
          </Button>
        </div>
      </div>

      {/* Role tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {(['admin', 'worker'] as RoleTab[]).map(role => (
          <button
            key={role}
            onClick={() => setRoleTab(role)}
            className={`px-5 py-2.5 text-sm font-semibold transition-colors border-b-2 ${
              roleTab === role
                ? 'text-blue-600 border-blue-500'
                : 'text-gray-500 border-transparent hover:text-gray-800'
            }`}
          >
            {ROLE_LABEL[role]}
          </button>
        ))}
      </div>

      {/* Catalog groups */}
      <div className="space-y-6">
        {grouped.map(group => (
          <div key={group.group} className="bg-white rounded-xl shadow-sm border border-gray-100">
            {/* Group header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span className="text-sm font-semibold text-gray-700">{group.label}</span>
              <div className="flex gap-2">
                <button
                  className="text-xs text-blue-500 hover:underline"
                  onClick={() => group.items.forEach(i => toggle(roleTab, i.endpoint, true))}
                >
                  Все
                </button>
                <span className="text-gray-300">·</span>
                <button
                  className="text-xs text-gray-400 hover:underline"
                  onClick={() => group.items.forEach(i => toggle(roleTab, i.endpoint, false))}
                >
                  Ни одного
                </button>
              </div>
            </div>

            {/* Items */}
            <ul className="divide-y divide-gray-50">
              {group.items.map(item => {
                const isEnabled = currentEnabled?.[item.endpoint] ?? item.enabled
                return (
                  <li key={item.endpoint}>
                    <label className="flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={isEnabled}
                        onChange={e => toggle(roleTab, item.endpoint, e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-400 cursor-pointer"
                      />
                      {/* Icon + label */}
                      <span className="inline-flex w-6 justify-center text-gray-500">
                        <SidebarApiIcon icon={item.icon} label={item.label} className="h-4 w-4" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-gray-800">{item.label}</span>
                        <span className="ml-2 text-xs text-gray-400 font-mono">{item.path}</span>
                      </div>
                      {/* Visual indicator */}
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          isEnabled
                            ? 'bg-green-50 text-green-600'
                            : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {isEnabled ? 'видим' : 'скрыт'}
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>

      {/* Footer summary */}
      <div className="mt-6 flex items-center justify-between text-sm text-gray-500">
        <span>
          Показано <strong className="text-gray-700">{enabledCount}</strong> из{' '}
          <strong className="text-gray-700">{currentCatalog?.length ?? 0}</strong> разделов
        </span>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => toggleAll(roleTab, true)}>Показать все</Button>
          <Button variant="secondary" size="sm" onClick={() => toggleAll(roleTab, false)}>Скрыть все</Button>
        </div>
      </div>
    </div>
  )
}
